import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { LOGO_URL } from '../mock';
import ChangePasswordDialog from './ChangePasswordDialog';
import PMSyncStatusDot from './PMSyncStatusDot';
import {
  Boxes,
  Package,
  FolderKanban,
  LogOut,
  Search,
  ChevronDown,
  Lock,
  FileSearch,
  Palette,
  HardDrive,
  Menu,
  X,
} from 'lucide-react';

function NavItem({ to, icon: Icon, label, badge, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
          isActive
            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
            : 'text-neutral-300 hover:bg-neutral-900 hover:text-white border border-transparent'
        }`
      }
    >
      <Icon size={16} className="shrink-0" />
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-neutral-900 font-semibold">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout, items, projects } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [globalSearch, setGlobalSearch] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const activeProjects = projects.filter((p) => p.status === 'active');
  // Media awaiting user action — surfaces as a sidebar badge so the user
  // notices returned cards even if they didn't get a chance to open the page.
  const mediaAwaitingAction = items.filter(
    (i) => i.category === 'Media' && (i.mediaStatus === 'Needs Offload' || i.mediaStatus === 'Verifying'),
  ).length;

  // Auto-close the mobile drawer whenever the route changes so the user
  // isn't left staring at the drawer after tapping a nav item.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const navContent = (
    <>
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 px-2 mb-2">Workspace</div>
      <nav className="space-y-1">
        <NavItem to="/" end icon={FolderKanban} label="Projects" badge={activeProjects.length} />
        <NavItem to="/inventory" icon={Boxes} label="Main Inventory" />
        <NavItem to="/kits" icon={Package} label="Kit Inventory" />
        <NavItem
          to="/media"
          icon={HardDrive}
          label="Media Management"
          badge={mediaAwaitingAction || null}
        />
        <NavItem to="/reports/code-gaps" icon={FileSearch} label="Code Gaps" />
        <NavItem to="/settings/categories" icon={Palette} label="Categories" />
      </nav>

      {activeProjects.length > 0 && (
        <>
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 px-2 mt-6 mb-2">
            Active Projects
          </div>
          <nav className="space-y-1 max-h-64 overflow-auto">
            {activeProjects.slice(0, 8).map((p) => (
              <NavLink
                key={p.id}
                to={`/projects/${p.id}`}
                className={({ isActive }) =>
                  `block px-3 py-1.5 text-xs rounded-md transition-colors border ${
                    isActive
                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      : 'text-neutral-400 hover:bg-neutral-900 hover:text-white border-transparent'
                  }`
                }
              >
                <div className="truncate">{p.name}</div>
              </NavLink>
            ))}
          </nav>
        </>
      )}

      <div className="mt-auto pt-4">
        <div className="p-3 rounded-md border border-neutral-900 bg-neutral-950">
          <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">At a glance</div>
          <div className="text-xs text-neutral-300 flex justify-between py-0.5">
            <span>Items</span>
            <span className="text-white font-medium">{items.length}</span>
          </div>
          <div className="text-xs text-neutral-300 flex justify-between py-0.5">
            <span>Total units</span>
            <span className="text-white font-medium">{items.reduce((s, i) => s + i.totalQty, 0)}</span>
          </div>
          <div className="text-xs text-neutral-300 flex justify-between py-0.5">
            <span>Active projects</span>
            <span className="text-white font-medium">{activeProjects.length}</span>
          </div>
          <PMSyncStatusDot />
        </div>
      </div>
    </>
  );

  return (
    <div className="whip-bg min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="h-14 border-b border-neutral-900 bg-neutral-950/80 backdrop-blur sticky top-0 z-30 flex items-center px-4 gap-4">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="md:hidden -ml-1 p-2 rounded-md text-neutral-300 hover:bg-neutral-900 hover:text-white"
          aria-label="Open menu"
          data-testid="mobile-nav-open-btn"
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-3 md:min-w-[220px]">
          <img src={LOGO_URL} alt="Whiplash" className="h-7" />
          <div className="hidden md:block leading-tight">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Gear</div>
            <div className="text-xs text-neutral-300 -mt-0.5">Inventory</div>
          </div>
        </div>

        <div className="flex-1 max-w-xl relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && globalSearch.trim()) {
                const q = encodeURIComponent(globalSearch.trim());
                // Stay on the current section if it supports filtering;
                // default to Inventory otherwise.
                const target = location.pathname.startsWith('/kits')
                  ? `/kits?q=${q}`
                  : `/inventory?q=${q}`;
                navigate(target);
              }
            }}
            placeholder="Search gear, kits, codes…"
            className="w-full bg-neutral-900 border border-neutral-800 text-sm text-white pl-9 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60"
          />
        </div>

        <div className="flex items-center gap-3 relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
            className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-neutral-900 transition-colors"
          >
            <div className="hidden md:flex flex-col items-end leading-tight">
              <div className="text-xs text-neutral-200">{user?.name}</div>
              <div className="text-[10px] text-neutral-500">{user?.email}</div>
            </div>
            <ChevronDown size={13} className="text-neutral-500" />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 surface rounded-md shadow-2xl z-50 py-1 border border-neutral-800">
              <div className="px-3 py-2 border-b border-neutral-900">
                <div className="text-xs text-white font-medium truncate">{user?.name}</div>
                <div className="text-[10px] text-neutral-500 truncate">{user?.email}</div>
              </div>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setPwdOpen(true);
                  setUserMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-xs text-neutral-300 hover:bg-neutral-900 hover:text-white flex items-center gap-2"
              >
                <Lock size={12} /> Change Password
              </button>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  logout();
                  navigate('/login');
                }}
                className="w-full text-left px-3 py-2 text-xs text-neutral-300 hover:bg-neutral-900 hover:text-white flex items-center gap-2 border-t border-neutral-900"
              >
                <LogOut size={12} /> Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Desktop sidebar */}
        <aside className="w-60 shrink-0 border-r border-neutral-900 bg-neutral-950/60 p-3 hidden md:flex md:flex-col">
          {navContent}
        </aside>

        {/* Mobile drawer */}
        {mobileNavOpen && (
          <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-nav-drawer">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setMobileNavOpen(false)}
              aria-hidden="true"
            />
            <aside
              className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] border-r border-neutral-900 bg-neutral-950 p-3 flex flex-col overflow-y-auto shadow-2xl"
              role="dialog"
              aria-label="Navigation"
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <img src={LOGO_URL} alt="Whiplash" className="h-7" />
                <button
                  onClick={() => setMobileNavOpen(false)}
                  className="p-2 rounded-md text-neutral-400 hover:bg-neutral-900 hover:text-white"
                  aria-label="Close menu"
                  data-testid="mobile-nav-close-btn"
                >
                  <X size={18} />
                </button>
              </div>
              {navContent}
            </aside>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0">
          <Outlet context={{ globalSearch }} />
        </main>
      </div>

      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}
